import { useFetchAccountDetails } from '@/containers/Accounts/api';
import { selectAllAccounts, selectCurrentAccountId } from '@/lib/redux/app.selectors';
import { setAccountDetails, setAllAccounts } from '@/lib/redux/app.slice';
import { useAppDispatch } from '@/lib/redux/store';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import useUser from './useUser';

const useInitializeFetchAccount = () => {
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useUser();
  const currentAccountId = useSelector(selectCurrentAccountId);
  const allAccounts = useSelector(selectAllAccounts);
  const { isLoading, data } = useFetchAccountDetails(
    currentAccountId || undefined,
    isLoggedIn && !!currentAccountId
  );

  // Track which accounts we've already updated to prevent infinite loops
  const updatedAccountsRef = useRef<Set<string>>(new Set());

  // Update redux
  useEffect(() => {
    if (data && !isLoading && data.data) {
      dispatch(setAccountDetails(data.data));

      // Also update the account in allAccounts to include parent_account_key
      // Only if we haven't already updated this account's details
      if (
        allAccounts &&
        currentAccountId &&
        !updatedAccountsRef.current.has(currentAccountId)
      ) {
        const currentAccount = allAccounts.find(
          (acc) => acc.account_id === currentAccountId
        );

        // Only update if the account exists and needs updating
        if (
          currentAccount &&
          (currentAccount.parent_account_key !== data.data.parent_account_key ||
            currentAccount.parent_account_id !== data.data.parent_account_id)
        ) {
          const updatedAccounts = allAccounts.map((acc) =>
            acc.account_id === currentAccountId
              ? {
                  ...acc,
                  parent_account_id: data.data.parent_account_id || acc.parent_account_id,
                  parent_account_key:
                    data.data.parent_account_key || acc.parent_account_key,
                }
              : acc
          );
          dispatch(setAllAccounts(updatedAccounts));
          updatedAccountsRef.current.add(currentAccountId);
        }
      }
    }
  }, [data, isLoading, currentAccountId, dispatch, allAccounts]);

  // Clear the tracking ref when account changes
  useEffect(() => {
    updatedAccountsRef.current.clear();
  }, [currentAccountId]);
};

export default useInitializeFetchAccount;
